import React from "react";

/**
 * HexBackdrop
 * Subtle floating favicon watermarks for white / light sections.
 * Sits behind content, non-interactive. Uses very low opacity + slow
 * rotate & float so it feels ambient rather than decorative.
 *
 * Variants:
 *  - "sparse"   — 2 marks, quiet corners
 *  - "dense"    — 4 marks with a scattered pattern
 *  - "topRight" — single large mark for hero sections
 */
const MARKS = {
  sparse: [
    { top: "8%", left: "-3%", size: 220, rot: -12, delay: 0, opacity: 0.06, blur: 0 },
    { bottom: "10%", right: "-4%", size: 260, rot: 8, delay: 2, opacity: 0.05, blur: 0 },
  ],
  dense: [
    { top: "4%", left: "6%", size: 120, rot: -18, delay: 0, opacity: 0.055, blur: 0 },
    { top: "48%", right: "3%", size: 200, rot: 14, delay: 3, opacity: 0.07, blur: 0 },
    { bottom: "6%", left: "18%", size: 90, rot: 22, delay: 1.5, opacity: 0.05, blur: 1 },
    { top: "22%", left: "62%", size: 60, rot: -6, delay: 4.5, opacity: 0.04, blur: 0 },
  ],
  topRight: [
    { top: "-10%", right: "-8%", size: 420, rot: -18, delay: 0, opacity: 0.06, blur: 1 },
  ],
};

export default function HexBackdrop({ variant = "sparse", src = "/favicon.png", className = "" }) {
  const marks = MARKS[variant] || MARKS.sparse;
  return (
    <div
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}
    >
      {marks.map((m, i) => (
        <img
          key={i}
          src={src}
          alt=""
          className="absolute select-none will-change-transform"
          style={{
            top: m.top,
            left: m.left,
            right: m.right,
            bottom: m.bottom,
            width: `${m.size}px`,
            height: `${m.size}px`,
            opacity: m.opacity,
            filter: `blur(${m.blur || 0}px) grayscale(15%)`,
            "--hex-rot": `${m.rot}deg`,
            transform: `rotate(${m.rot}deg)`,
            animation: `hexbackdrop-float ${18 + i * 3}s ease-in-out ${m.delay}s infinite alternate`,
          }}
        />
      ))}
    </div>
  );
}
